"use client";

import { useState } from "react";
import { Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { describeAction, executeAction, type ParsedAction } from "@/lib/actions";
import { track, captureError } from "@/lib/telemetry";

type Status = "pending" | "applying" | "applied" | "error";

export function ActionCard({
  action,
  sessionId,
}: {
  action: ParsedAction;
  sessionId: string | null;
}) {
  const [status, setStatus] = useState<Status>("pending");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // select_range is fire-and-forget — auto-apply on render with no UI button
  if (action.type === "select_range") {
    return <SelectRangeApplier action={action} sessionId={sessionId} />;
  }

  async function handleApply() {
    setStatus("applying");
    setErrorMsg(null);
    try {
      await executeAction(action);
      setStatus("applied");
      track("excel.action_applied", { action_type: action.type, session_id: sessionId });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Apply failed";
      setStatus("error");
      setErrorMsg(msg);
      captureError(e, { where: "ActionCard.apply", action_type: action.type });
    }
  }

  function handleDismiss() {
    setStatus("applied"); // visually collapse
    track("excel.action_dismissed", { action_type: action.type, session_id: sessionId });
  }

  return (
    <div className="my-2 rounded-xl border border-[var(--border-accent)] bg-[var(--surface-overlay)] p-3 text-[13px]">
      <div className="mb-2 text-[var(--text-secondary)]">{describeAction(action)}</div>

      {status === "pending" && (
        <div className="flex gap-2">
          <Button onClick={handleApply} size="sm">
            Apply
          </Button>
          <button
            onClick={handleDismiss}
            className="px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            Dismiss
          </button>
        </div>
      )}

      {status === "applying" && (
        <div className="text-[var(--text-muted)]">Applying…</div>
      )}

      {status === "applied" && (
        <div className="flex items-center gap-1 text-[var(--status-complete)]">
          <Check size={14} strokeWidth={1.5} />
          <span>Applied · undo with ⌘Z</span>
        </div>
      )}

      {status === "error" && (
        <div className="flex items-start gap-1 text-[var(--status-error)]">
          <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
          <span>{errorMsg ?? "Apply failed"}</span>
        </div>
      )}
    </div>
  );
}

function SelectRangeApplier({
  action,
  sessionId,
}: {
  action: ParsedAction & { type: "select_range" };
  sessionId: string | null;
}) {
  const [status, setStatus] = useState<"pending" | "navigated" | "error">("pending");

  // Fire-and-forget on first render. No button — agent navigated, chat narrates.
  // We use a useEffect-equivalent inline gate to avoid double-execution under React strict mode.
  if (typeof window !== "undefined" && status === "pending") {
    setStatus("navigated"); // optimistic — flip immediately
    executeAction(action)
      .then(() => track("excel.action_applied", { action_type: "select_range", session_id: sessionId }))
      .catch((e) => {
        setStatus("error");
        captureError(e, { where: "ActionCard.selectRange" });
      });
  }

  if (status === "error") {
    return (
      <div className="my-1 text-[12px] text-[var(--status-error)]">
        Couldn't navigate to {action.address}.
      </div>
    );
  }
  return (
    <div className="my-1 text-[12px] text-[var(--text-muted)]">
      Selected {action.address}
    </div>
  );
}
